import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  FactoryManageVacationsProposalsDto,
  FactoryCompensationDto
} from '@app/data/dtos/manage-vacations/factory/factory-manage-vacations.dto';
import {
  ManageVacationsCriteriaDto,
  EmployeeObservationsDto,
  VacationsSeasonDto,
  SeasonCalendarDto
} from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import {
  mapFactoryCreateProposalDto,
  mapFactoryVacationsProposals,
  mapFactoryCompensation,
  mapFactoryCompensationDto,
  mapSeasonCalendarToAnualCalendarMonths
} from '@app/data/mappers/manage-vacations/factory/factory-manage-vacations.mapper';
import {
  mapManageVacationsCriteria,
  mapObservations,
  mapSeasonCalendar,
  mapVacationsSeason
} from '@app/data/mappers/manage-vacations/manage-vacations.mapper';
import { FactoryManageVacationsRepository } from '@app/domain/repositories/manage-vacations/factory/factory-manage-vacations.repository.contract';
import { ManageVacationsUserType, SEASONS } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import { AnualCalendarMonth } from '@app/entities/models/lababs/lababs-vacations.model';
import {
  FactoryManageVacationsProposals,
  FactoryCompensation
} from '@app/entities/models/manage-vacations/factory-manage-vacations.model';
import { ManageVacationsCriteria } from '@app/entities/models/manage-vacations/manage-vacations-criteria.model';
import {
  EmployeeObservations,
  SeasonCalendar,
  VacationsSeason
} from '@app/entities/models/manage-vacations/manage-vacations.model';
import { MotFactoryProposal } from '@app/entities/models/manage-vacations/mot-manage-vacations.model';
import { ENV } from '@environments/environment';
import {
  IGNORE_NO_CONTENT_ERROR,
  SECTION_KEY
} from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

import { TUserTypeCalendarProposal } from '@/types/manage-vacations.type';

@Injectable({
  providedIn: 'root'
})
export class FactoryManageVacationsRepositoryImpl extends MRepository implements FactoryManageVacationsRepository {
  readonly factoryBaseUrl = '/timemanage/v1/factory/vacations-proposal';

  constructor() {
    super(`${ENV.API_URL}`);
  }

  getProposals(): Observable<FactoryManageVacationsProposals> {
    return this.get<FactoryManageVacationsProposalsDto>(this.factoryBaseUrl, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations').set(IGNORE_NO_CONTENT_ERROR, true)
    }).pipe(map(mapFactoryVacationsProposals));
  }

  getVacationsCriteria(): Observable<ManageVacationsCriteria> {
    return this.get<ManageVacationsCriteriaDto>(`${this.factoryBaseUrl}/criteria`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapManageVacationsCriteria));
  }

  getVacationsSeason(): Observable<VacationsSeason> {
    return this.get<VacationsSeasonDto>(`${this.factoryBaseUrl}/seasons`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapVacationsSeason));
  }

  getSeasonCalendar(season: SEASONS): Observable<SeasonCalendar<TUserTypeCalendarProposal>> {
    let params = new HttpParams();
    params = params.set('season', season);

    return this.get<SeasonCalendarDto>(`${this.factoryBaseUrl}/season-calendar`, {
      params,
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(map(mapSeasonCalendar(ManageVacationsUserType.BREAD_FACTORY)));
  }

  createProposal(
    proposals: FactoryManageVacationsProposals,
    storedProposals: MotFactoryProposal[],
    isSummerSeason: boolean
  ): Observable<FactoryManageVacationsProposals> {
    const createProposal = mapFactoryCreateProposalDto(proposals, storedProposals, isSummerSeason);

    const request = {
      body: {
        ...createProposal
      }
    };

    return this.post<FactoryManageVacationsProposalsDto>(this.factoryBaseUrl, request).pipe(
      map(mapFactoryVacationsProposals)
    );
  }

  updateObservations(observations: string): Observable<EmployeeObservations> {
    const request = {
      body: {
        content: observations
      }
    };

    return this.put<EmployeeObservationsDto>(`${this.factoryBaseUrl}/observations`, request).pipe(map(mapObservations));
  }

  getCompensations(): Observable<FactoryCompensation> {
    return this.get<FactoryCompensationDto>(`${this.factoryBaseUrl}/compensation`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations-compensation').set(IGNORE_NO_CONTENT_ERROR, true)
    }).pipe(map(mapFactoryCompensation));
  }

  getCalendar(): Observable<AnualCalendarMonth[]> {
    return this.get<SeasonCalendarDto>(`${this.factoryBaseUrl}/calendar`, {
      context: new HttpContext().set(SECTION_KEY, 'manage-vacations')
    }).pipe(
      map(mapSeasonCalendar(ManageVacationsUserType.PALLET_FACTORY)),
      map(mapSeasonCalendarToAnualCalendarMonths)
    );
  }

  createCompensation(compensations: FactoryCompensation): Observable<FactoryCompensation> {
    const createCompensation = mapFactoryCompensationDto(compensations);

    const request = {
      body: {
        ...createCompensation
      }
    };

    return this.post<FactoryCompensationDto>(`${this.factoryBaseUrl}/compensation`, request).pipe(
      map(mapFactoryCompensation)
    );
  }
}
